import { InfoOutlined, MoreVert, PeopleOutline, VideoCallOutlined } from "@material-ui/icons";
import { Box, Collapse, Grow, IconButton, Menu, MenuItem } from "@mui/material";
import { collection, doc, getDoc, getDocs, onSnapshot, query, updateDoc, where } from "firebase/firestore";
import { useStreamVideoClient } from "@stream-io/video-react-sdk";
import { shape, string, any, array, bool } from "prop-types";
import { useCallback, useContext, useEffect, useState } from "react";
import _ from "lodash";
import { v4 } from "uuid";
import { useTranslation } from "react-i18next";
import CallBar from "../call-layer/call-bar";
import MembersManagmentDialog from "./MembersManagmentDialog";
import useChatCall from "../../hooks/useChatCall";
import { upsertUsers } from "../../services/call_service";
import UserContext from "../../contexts/UserContext";
import ProgramInfoPopupButton from "./ProgramInfoPopupButton";
import SessionSchedulingMenuItem from "./SessionSchedulingMenuItem";
import { db } from "../../firebaseConfig";
import { createSession } from "../../services/sessions_service";
import Notification from "../notification";
import PlannedSessionsBar from "./PlannedSessionsBar";
import AVCallContext from "../../contexts/AVCallContext";

ChatRightHeader.propTypes = {
    collectionName: string,
    chat: shape({
        id: string,
        name: string,
        callInfos: shape({
            id: string,
            start_at: any
        })
    }),
    members: array
};
export default function ChatRightHeader({ collectionName, chat, members }) {
    const { user } = useContext(UserContext);
    const { currentCall, setCurrentCall } = useContext(AVCallContext);
    const { t } = useTranslation();
    const client = useStreamVideoClient();
    const chatCall = useChatCall(chat.id);

    const [callMenuAnchor, setCallMenuAnchor] = useState(null);
    const [membersDialogOpen, setMembersDialogOpen] = useState(false);
    const [snackData, setSnackData] = useState({
        isOpen: false,
        msg: '',
        isSuccess: true,
    });

    const [progReg, setProgReg] = useState(null);
    const [program, setProgram] = useState(null);
    const [sessions, setSessions] = useState(null);
    const [plannedSessions, setPlannedSessions] = useState(null);
    const [isLoading, setIsLoading] = useState(true);


    const handleCallMenuClose = () => setCallMenuAnchor(null);

    const handleCloseSnack = () => setSnackData((prev) => ({ ...prev, isOpen: false }));

    const handleStartCallClick = async (type, ring = true) => {
        const _callId = v4();
        const call = client.call('default', _callId);

        try {
            return await call.getOrCreate({
                ring,
                data: {
                    members: members.map((m) => ({ user_id: m.id })),
                    custom: {
                        name: "Voip call",
                        chatId: chat.id,
                        chatName: chat.name
                    },
                    video: type === 'video',
                }
            })
        } catch (error) {
            console.error("Error starting call", error);

            if (error.code === 4) {
                upsertUsers(members.map((m) => ({ id: m.id, name: m.name, image: m.image })))
                    .then(() => {
                        handleStartCallClick(type);
                    })
                    .catch((error) => console.error("Error upserting users", error));
            }
        }
        handleCallMenuClose();
        return null;
    }

    const handleCreateSession = async (data) => {
        const selectedSession = sessions.find(s => s.id === data.selectedSessionId);
        const durationInMinutes = selectedSession.duration * ({
            'Minutes': 1,
            'Hours': 60,
            'Days': 24 * 60,
            'Weeks': 24 * 60 * 7,
            'Months': 24 * 60 * 30
        }[selectedSession.duration_unit])
        const payload = {
            coachId: progReg.coach_id || user.id,
            programId: program.id,
            programRegistrationId: progReg.id,
            sessionTypeId: data.selectedSessionId,
            chatId: chat.id,
            sessionKind: data.selectedKind,
            sessionDate: data.date,
            sessionTime: data.time,
            durationInMinutes
        }

        await createSession(payload);

        setSnackData({
            isOpen: true,
            isSuccess: true,
            msg: 'Session created successfully'
        });
    }


    const handleStartSession = async (session) => {
        const _callId = v4();
        const call = client.call('default', _callId);

        try {
            await call.join({
                create: true,
                data: {
                    members: members.map((m) => ({ user_id: m.id })),
                    custom: {
                        name: session.name,
                        chatId: chat.id,
                        chatName: chat.name
                    }
                }
            })
            console.log("Call started successfully", call);
            setCurrentCall(call);
            await updateSessionStatus(session.id, 'STARTED', {
                call_id: call.id
            });
        } catch (error) {
            console.error("Error starting call", error);

            if (error.code === 4) {
                upsertUsers(members.map((m) => ({ id: m.id, name: m.name, image: m.image })))
                    .then(() => {
                        handleStartSession(session);
                    })
                    .catch((error) => console.error("Error upserting users", error));
            }
        }
    }

    const updateSessionStatus = useCallback(async (sessionId, status, data = {}) => {
        const sessionRef = doc(db, 'sessions', sessionId);
        await updateDoc(sessionRef, { status, ...data });
    }, []);

    useEffect(() => {
        if (!chat.id) {
            setIsLoading(false);
            return () => { };
        }

        const unsubscribe = onSnapshot(
            query(collection(db, 'programs_registration'), where("chat_id", "==", chat.id)),
            async (snapshot) => {
                try {
                    if (snapshot.empty) {
                        throw new Error("Program registration not found");
                    }

                    const progRegData = {
                        id: snapshot.docs[0].id,
                        ...snapshot.docs[0].data()
                    };

                    setProgReg(progRegData);

                    if (!program || program.id !== progRegData.program_id) {
                        const programDoc = await getDoc(doc(db, 'programs', progRegData.program_id));
                        if (programDoc.exists()) {
                            setProgram({ id: programDoc.id, ...programDoc.data() });
                        } else {
                            throw new Error("Program not found");
                        }
                    }

                    if (!sessions) {
                        const sessionsSnap = await getDocs(collection(db, 'programs', progRegData.program_id, 'sessions_type'));
                        const sessionsData = sessionsSnap.docs.map(doc => doc.data());
                        setSessions(sessionsData);
                    }

                    if (isLoading) setIsLoading(false);
                } catch (error) {
                    console.error("Error fetching program registration", error);
                    setProgReg(null);
                    setProgram(null);
                    setSessions(null);
                }
            },
            (error) => {
                console.error("Error subscribing to programs_registration", error);
            }
        );

        return () => unsubscribe();
    }, [chat.id, isLoading, program, sessions]);

    useEffect(() => {
        if (!progReg || _.isEmpty(progReg.sessions_ids)) {
            setPlannedSessions(null);
            return () => { };
        }

        const unsubscribe = onSnapshot(query(
            collection(db, 'sessions'),
            where('id', 'in', progReg.sessions_ids),
            where('status', '!=', 'COMPLETED'),
        ), (snapshot) => {
            const plannedSessionsData = [];
            snapshot.forEach(doc => {
                plannedSessionsData.push({
                    id: doc.id,
                    ...doc.data()
                });
            });
            setPlannedSessions(plannedSessionsData);
        });

        return () => unsubscribe();
    }, [progReg]);


    const pSessions = sessions ? progReg?.package.content.map(p => {
        const session = sessions.find(s => s.id === p.session_type_id);
        return {
            ...session, /* name, duration, duration_unit */
            count: p.count,
            remainingCount: p.remainingCount,
        }
    }) : [];
    const isSessionPlanned = plannedSessions && plannedSessions.some(s => !s.status); // status is empty if not started
    const callMenuOpen = Boolean(callMenuAnchor);
    const cannotCreateCall = Boolean(chatCall);
    const showCallButton = collectionName && ['chats'].includes(collectionName);
    const showManageUser = collectionName && user.userRoles.includes('ADMIN');
    const showInfoButton = collectionName && ['chats'].includes(collectionName);


    return (
        <>
            <div style={styles.header}>
                <div style={styles.chatNameContainer}>
                    <Grow in key={chat?.name}>
                        <h2 style={styles.chatName}>{chat?.name}</h2>
                    </Grow>
                </div>

                {
                    showCallButton && (
                        <IconButton
                            id="call-menu"
                            aria-label="call"
                            aria-controls={callMenuOpen ? 'call-menu' : undefined}
                            aria-haspopup="true"
                            aria-expanded={callMenuOpen ? 'true' : undefined}
                            onClick={(event) => setCallMenuAnchor(event.currentTarget)}
                        >
                            <VideoCallOutlined />
                        </IconButton>
                    )
                }
                <Menu
                    id="call-menu"
                    anchorEl={callMenuAnchor}
                    open={callMenuOpen}
                    onClose={handleCallMenuClose}
                    MenuListProps={{
                        'aria-labelledby': 'call-menu',
                    }}
                >
                    <MenuItem onClick={() => handleStartCallClick('video')} disabled={cannotCreateCall}>{t("st_vidcall")}</MenuItem>
                    <MenuItem onClick={() => handleStartCallClick('audio')} disabled={cannotCreateCall}>{t("st_audcall")}</MenuItem>
                    <SessionSchedulingMenuItem
                        sessions={pSessions}
                        onCreateClick={handleCreateSession}
                        onClose={handleCallMenuClose}
                        disabled={cannotCreateCall || isSessionPlanned}
                    />
                </Menu>

                <IconButton aria-label="call" onClick={() => setMembersDialogOpen(true)}>
                    <PeopleOutline />
                </IconButton>
                <MembersManagmentDialog
                    disabled={!showManageUser}
                    collection={collectionName}
                    chat={chat}
                    open={membersDialogOpen}
                    onClose={() => setMembersDialogOpen(false)}
                    currentMembers={members}
                />
                {
                    showInfoButton && (
                        <ProgramInfoPopupButton
                            progReg={progReg}
                            program={program}
                            sessions={pSessions}
                            isLoading={isLoading}
                        />
                    )
                }
            </div>
            <Collapse in={Boolean(chatCall || plannedSessions)} sx={{ width: '100%', display: 'flex', gap: '1rem' }}>
                {chatCall && (
                    <Box flex={1}>
                        <CallBar call={chatCall} />
                    </Box>
                )}
                {!_.isEmpty(plannedSessions) && (
                    <Box flex={1}>
                        <PlannedSessionsBar
                            sessions={plannedSessions}
                            onClickStartSession={handleStartSession}
                            updateSessionStatus={updateSessionStatus}
                        />
                    </Box>
                )}
            </Collapse>
            <Notification
                open={snackData.isOpen}
                success={snackData.isSuccess}
                message={t(snackData.msg)}
                onClose={handleCloseSnack}
            />
        </>
    );
}

const styles = {
    header: {
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        padding: '.5rem 1rem',
        backgroundColor: '#f5f5f5',
        borderBottom: '1px solid #e0e0e0',
        gap: '1rem'
    },
    chatNameContainer: {
        position: 'absolute',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
    },
    chatName: {
        fontSize: '1rem',
        margin: 0,
        padding: 0,
        textAlign: 'center',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        width: '55rem'
    }
}